/**
 * Vault setup (Phase 9): create a new vault (12-word recovery phrase, written down and checked)
 * or import one (recovery phrase or a private key). Everything here runs in the browser; nothing
 * typed on these screens is sent anywhere.
 */
import { APP_SHORT_NAME } from "@custos/core";
import {
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Notice,
  Segmented,
  Select,
  Skeleton,
  Textarea,
} from "@custos/ui";
import {
  generateRecoveryPhrase,
  KEY_FAMILIES,
  parseMnemonic,
  privateKeySecret,
  type KeyFamily,
  type VaultSecret,
} from "@custos/vault";
import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router";
import { PageHeader } from "../components/AppShell";
import { BrowserVaultWarning, NewPasswordFields, SecretInput, WordGrid } from "../components/VaultBits";
import { errorMessage } from "../lib/errors";
import { useConfig, useVaultRecovery } from "../lib/queries";
import { disableRecovery, syncRecoveryBackup, useVault } from "../lib/vault";
import { pickCheckPositions } from "../lib/backup-check";
import { FAMILY_NAME, familyChains, privateKeyHint, useVaultSession } from "../lib/vault-ui";

/** Final step of both flows: choose the password, then encrypt and save. */
function PasswordStep({ secret, onBack }: { secret: VaultSecret; onBack: () => void }) {
  const navigate = useNavigate();
  const create = useVault((s) => s.create);
  const [password, setPassword] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const recovery = useVaultRecovery();
  const recoveryAvailable = recovery.data?.available === true;
  // On by default (D70); the user can untick it.
  const [recoverable, setRecoverable] = useState(true);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!password) return;
    setBusy(true);
    setError(null);
    try {
      await create(secret, password);
      // In the background: the Vault page's Recovery card shows the result.
      if (recoveryAvailable) {
        if (recoverable) void syncRecoveryBackup({ replace: true, turnOn: true });
        else void disableRecovery().catch(() => undefined);
      }
      navigate("/vault", { replace: true });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <CardTitle>Set your vault password</CardTitle>
      <p className="text-sm text-muted">
        You'll type it to unlock the vault on this device. It can't be reset — if you forget it, remove the
        vault and import your recovery phrase again.
      </p>
      <NewPasswordFields onChange={setPassword} touched={touched} />
      {recoveryAvailable && (
        <label className="flex items-start gap-3 rounded-field border border-line bg-soft p-3 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-0.5 size-5 shrink-0 accent-[var(--primary)]"
            checked={recoverable}
            onChange={(e) => setRecoverable(e.target.checked)}
          />
          <span>
            <b>Let {APP_SHORT_NAME} support recover this vault</b> if I lose my recovery phrase and this
            device.
            <span className="mt-1 block text-xs text-muted">
              An encrypted copy is kept that only the {APP_SHORT_NAME} recovery key — held offline, not on our
              servers — can open, after an identity check and a {recovery.data?.waitHours ?? 24}-hour waiting
              period.
            </span>
          </span>
        </label>
      )}
      {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button type="button" variant="secondary" onClick={onBack} disabled={busy}>
          Back
        </Button>
        <Button type="submit" className="flex-1" loading={busy}>
          Encrypt and save vault
        </Button>
      </div>
    </form>
  );
}

function SetupFrame({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  const vault = useVaultSession();
  if (vault.status === "loading") return <Skeleton className="h-64 rounded-card" />;
  // One vault per device (D63): setup only when there is none.
  if (vault.status !== "none") return <Navigate to="/vault" replace />;
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <BrowserVaultWarning />
        <Card className="animate-rise">{children}</Card>
      </div>
    </>
  );
}

// ───────────────────────── Create ─────────────────────────

export function VaultCreatePage() {
  const [phrase] = useState(() => generateRecoveryPhrase());
  const words = phrase.split(" ");
  const [step, setStep] = useState<"write" | "check" | "password">("write");
  const [revealed, setRevealed] = useState(false);
  const [wroteDown, setWroteDown] = useState(false);
  const [positions] = useState(() => pickCheckPositions(words.length));
  const [answers, setAnswers] = useState<string[]>(["", "", ""]);
  const [checkError, setCheckError] = useState(false);

  const check = (e: FormEvent) => {
    e.preventDefault();
    const ok = positions.every((pos, i) => answers[i]?.trim().toLowerCase() === words[pos - 1]);
    setCheckError(!ok);
    if (ok) setStep("password");
  };

  return (
    <SetupFrame title="Create a vault" subtitle="A new self-custody wallet, kept only on this device.">
      {step === "write" && (
        <div className="flex flex-col gap-4">
          <CardTitle>Write down your recovery phrase</CardTitle>
          <p className="text-sm text-muted">
            These 12 words are the <b>only</b> backup of this wallet. Write them on paper, in order, and keep
            them somewhere safe and offline. Never type them into a website, chat or email, and never share
            them — anyone with them can take your funds.
          </p>
          {revealed ? (
            <WordGrid words={words} />
          ) : (
            <button
              type="button"
              onClick={() => setRevealed(true)}
              className="flex min-h-40 items-center justify-center rounded-field border border-dashed border-line bg-soft text-sm text-fg hover:bg-hover"
            >
              Make sure nobody can see your screen, then tap to show the words
            </button>
          )}
          <label className="flex min-h-11 items-center gap-3 text-sm text-fg">
            <input
              type="checkbox"
              className="size-5 accent-[var(--primary)]"
              checked={wroteDown}
              onChange={(e) => setWroteDown(e.target.checked)}
              disabled={!revealed}
            />
            I have written down all 12 words, in order.
          </label>
          <Button onClick={() => setStep("check")} disabled={!revealed || !wroteDown} block>
            Continue
          </Button>
        </div>
      )}

      {step === "check" && (
        <form onSubmit={check} className="flex flex-col gap-4" noValidate>
          <CardTitle>Check your backup</CardTitle>
          <p className="text-sm text-muted">Type these words from your paper copy.</p>
          {positions.map((pos, i) => (
            <Field key={pos} label={`Word #${pos}`}>
              {(p) => (
                <Input
                  {...p}
                  value={answers[i]}
                  onChange={(e) => setAnswers((a) => a.map((v, j) => (j === i ? e.target.value : v)))}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className="font-mono"
                />
              )}
            </Field>
          ))}
          {checkError && (
            <Notice tone="danger">
              One or more words don't match. Check your paper copy — or go back and look at the words again.
            </Notice>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button type="button" variant="secondary" onClick={() => setStep("write")}>
              Show the words again
            </Button>
            <Button type="submit" className="flex-1">
              Check
            </Button>
          </div>
        </form>
      )}

      {step === "password" && (
        <PasswordStep secret={{ kind: "mnemonic", mnemonic: phrase }} onBack={() => setStep("check")} />
      )}
    </SetupFrame>
  );
}

// ───────────────────────── Import ─────────────────────────

export function VaultImportPage() {
  const config = useConfig();
  const mode = config.data?.networkMode ?? "testnet";
  const chains = config.data?.chains ?? [];
  const [method, setMethod] = useState<"phrase" | "key">("phrase");
  const [phrase, setPhrase] = useState("");
  const [family, setFamily] = useState<KeyFamily>("EVM");
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState<VaultSecret | null>(null);
  const [error, setError] = useState<unknown>(null);

  const next = (e: FormEvent) => {
    e.preventDefault();
    try {
      setSecret(
        method === "phrase"
          ? { kind: "mnemonic", mnemonic: parseMnemonic(phrase) }
          : privateKeySecret(family, key, mode),
      );
      setError(null);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <SetupFrame title="Import a wallet" subtitle="Bring an existing wallet into your vault on this device.">
      {secret ? (
        <PasswordStep secret={secret} onBack={() => setSecret(null)} />
      ) : (
        <form onSubmit={next} className="flex flex-col gap-4" noValidate>
          <Segmented
            label="Import with"
            value={method}
            onChange={(m) => {
              setMethod(m);
              setError(null);
            }}
            className="grid w-full grid-cols-2 [&>button]:w-full"
            options={[
              { value: "phrase", label: "Recovery phrase" },
              { value: "key", label: "Private key" },
            ]}
          />
          {method === "phrase" ? (
            <Field
              label="Recovery phrase"
              hint="12 or 24 words, separated by spaces. Gives you all chains (account 1 in MetaMask, TronLink, Phantom, Electrum…)."
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  rows={4}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="font-mono"
                />
              )}
            </Field>
          ) : (
            <>
              <Field label="Chain" hint={familyChains(chains, family)}>
                {(p) => (
                  <Select {...p} value={family} onChange={(e) => setFamily(e.target.value as KeyFamily)}>
                    {KEY_FAMILIES.map((f) => (
                      <option key={f} value={f}>
                        {FAMILY_NAME[f]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Private key" hint={privateKeyHint(family, mode)}>
                {(p) => (
                  <SecretInput
                    {...p}
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    autoComplete="off"
                    className="font-mono"
                  />
                )}
              </Field>
              <p className="text-xs text-muted">
                A private key covers one chain. You can add keys for other chains later.
              </p>
            </>
          )}
          {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
          <Button type="submit" block disabled={method === "phrase" ? !phrase.trim() : !key.trim()}>
            Continue
          </Button>
        </form>
      )}
    </SetupFrame>
  );
}
